import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { ReactNode } from "react";
import {
  makePendingKey,
  type MatchAction,
  type MatchStatus,
  type PendingKey,
} from "./payment-inbox-utils";

// Shared building blocks for the read-only payment inboxes (PayPal, Mercury).
// Both inboxes have the same lifecycle — suggested → matched/rejected, all via
// manual admin actions — so the status badge, action buttons, and detail panel
// are shared to keep the two pages consistent. Non-component helpers/types live
// in ./payment-inbox-utils.

export function MatchStatusBadge({ status }: { status: MatchStatus }) {
  switch (status) {
    case "suggested":
      return (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
          Suggested
        </Badge>
      );
    case "matched":
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          Matched
        </Badge>
      );
    case "rejected":
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          Rejected
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground">
          Unmatched
        </Badge>
      );
  }
}

interface PaymentInboxActionsProps {
  id: string;
  matchStatus: MatchStatus;
  suggestedPoId?: string;
  pendingKeys: Set<PendingKey>;
  onAction: (id: string, action: MatchAction, poId?: string) => void;
  onOpenMatch: (id: string) => void;
  /** "row" keeps buttons on one line; "stacked" lets them wrap (detail panel). */
  layout?: "row" | "stacked";
}

/** Approve / Match… / Reject / Unmatch buttons, driven by match status. */
export function PaymentInboxActions({
  id,
  matchStatus,
  suggestedPoId,
  pendingKeys,
  onAction,
  onOpenMatch,
  layout = "row",
}: PaymentInboxActionsProps) {
  const wrapClass =
    layout === "stacked"
      ? "flex flex-wrap items-center gap-2"
      : "flex items-center justify-end gap-2 flex-nowrap";

  if (matchStatus === "suggested") {
    return (
      <div className={wrapClass}>
        <Button
          size="sm"
          variant="default"
          className="whitespace-nowrap"
          loading={pendingKeys.has(makePendingKey(id, "match"))}
          loadingText="Approving…"
          onClick={() => onAction(id, "match", suggestedPoId)}
        >
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="whitespace-nowrap"
          onClick={() => onOpenMatch(id)}
        >
          Match…
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="whitespace-nowrap text-muted-foreground hover:text-destructive"
          loading={pendingKeys.has(makePendingKey(id, "reject"))}
          loadingText="Rejecting…"
          onClick={() => onAction(id, "reject")}
        >
          Reject
        </Button>
      </div>
    );
  }

  if (matchStatus === "unmatched" || matchStatus === "rejected") {
    return (
      <div className={wrapClass}>
        <Button
          size="sm"
          variant="outline"
          className="whitespace-nowrap"
          onClick={() => onOpenMatch(id)}
        >
          Match…
        </Button>
        {matchStatus === "unmatched" && (
          <Button
            size="sm"
            variant="ghost"
            className="whitespace-nowrap text-muted-foreground hover:text-destructive"
            loading={pendingKeys.has(makePendingKey(id, "reject"))}
            loadingText="Rejecting…"
            onClick={() => onAction(id, "reject")}
          >
            Reject
          </Button>
        )}
      </div>
    );
  }

  if (matchStatus === "matched") {
    return (
      <div
        className={
          layout === "stacked" ? "flex" : "flex items-center justify-end"
        }
      >
        <Button
          size="sm"
          variant="ghost"
          className="whitespace-nowrap text-muted-foreground hover:text-destructive"
          loading={pendingKeys.has(makePendingKey(id, "unmatch"))}
          loadingText="Unmatching…"
          onClick={() => onAction(id, "unmatch")}
        >
          Unmatch
        </Button>
      </div>
    );
  }

  return null;
}

export interface PaymentDetailRow {
  label: string;
  value?: ReactNode;
}

interface PaymentDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  amount: ReactNode;
  fee?: ReactNode;
  statusBadge: ReactNode;
  rows: PaymentDetailRow[];
  /** Suggested / matched PO block. */
  poSection?: ReactNode;
  /** Action buttons. */
  actions?: ReactNode;
}

function isEmpty(value: ReactNode): boolean {
  return value === undefined || value === null || value === "";
}

/** Right-side detail panel showing the full metadata for one inbox payment. */
export function PaymentDetailSheet({
  open,
  onOpenChange,
  title,
  amount,
  fee,
  statusBadge,
  rows,
  poSection,
  actions,
}: PaymentDetailSheetProps) {
  const visibleRows = rows.filter((row) => !isEmpty(row.value));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
      >
        <div className="border-b px-6 py-5">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="truncate text-base">{title}</SheetTitle>
          </SheetHeader>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-mono text-2xl font-semibold">{amount}</span>
            {!isEmpty(fee) && (
              <span className="text-xs text-muted-foreground">fee: {fee}</span>
            )}
          </div>
          <div className="mt-3">{statusBadge}</div>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {poSection}
          <dl className="grid grid-cols-1 gap-x-4 gap-y-3">
            {visibleRows.map((row) => (
              <div key={row.label} className="grid grid-cols-3 gap-2">
                <dt className="text-xs text-muted-foreground">{row.label}</dt>
                <dd className="col-span-2 break-words text-sm">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        {actions && <div className="border-t px-6 py-4">{actions}</div>}
      </SheetContent>
    </Sheet>
  );
}
